import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useChat } from '../../contexts/ChatContext';
import { useVoice } from '../../contexts/VoiceContext';
import MessageInput from '../Chat/MessageInput';
import VoiceInterface from '../Chat/VoiceInterface';
import { Books, Exam, CaretRight } from '@phosphor-icons/react';

const EXAMPLES_BY_GRADE = {
  elementary: [
    { id: 'add-help', icon: 'Math', title: '"Help Me"', text: 'Help me with adding numbers', bg: 'bg-blue-600' },
    { id: 'plants', icon: 'SC', title: '"Tell Me"', text: 'What do plants need to grow?', bg: 'bg-green-600' },
    { id: 'story', icon: 'RD', title: '"Read"', text: 'Can you help me read a short story?', bg: 'bg-purple-600' },
    { id: 'spelling', icon: 'SP', title: '"Spelling"', text: 'Help me practice my spelling words', bg: 'bg-orange-600' },
  ],
  middle: [
    { id: 'explain-fractions', icon: 'Math', title: '"Explain"', text: 'Can you explain fractions in simple terms?', bg: 'bg-blue-600' },
    { id: 'solve-equation', icon: 'SC', title: '"How to"', text: 'How do I solve this equation: 2x + 5 = 15?', bg: 'bg-green-600' },
    { id: 'practice-problems', icon: 'QZ', title: '"Practice"', text: 'Give me 5 practice problems on addition', bg: 'bg-purple-600' },
    { id: 'science-experiment', icon: 'SC', title: '"Science"', text: 'Describe a simple science experiment I can do at home', bg: 'bg-orange-600' },
  ],
};

const ExampleIcon = ({ text, bg, size }) => (
  <div className={`${bg} text-white rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0`}
    style={{ width: size, height: size }}>
    {text}
  </div>
);

const WelcomeContent = ({ onExampleClick, onSendMessage, loading }) => {
  const { currentUser } = useAuth();
  const { createSession } = useChat();
  const { isVoiceMode, startVoice, stopVoice } = useVoice();
  const gradeLevel = currentUser?.grade_level || 'middle';
  const isElementary = gradeLevel === 'elementary';
  const examples = EXAMPLES_BY_GRADE[gradeLevel] || EXAMPLES_BY_GRADE.middle;
  const iconSize = isElementary ? 40 : 32;

  const handleVoiceToggle = async () => {
    if (isVoiceMode) {
      stopVoice();
    } else {
      const newSession = await createSession();
      if (newSession?.id) {
        startVoice(newSession.id);
      }
    }
  };

  const welcomeMessage = isElementary
    ? `Hi ${currentUser?.first_name || 'there'}! Ready to learn?`
    : 'Good day! How may I help with your learning today?';

  return (
    <div id="main-content" className="flex-1 flex flex-col h-full relative overflow-hidden">
      <div className="chat-container flex flex-col h-full overflow-hidden bg-white rounded-lg">
        <div className="chat-header shadow-sm py-4 px-6 flex items-center justify-between md:mt-0 mt-8">
          <div className="logo-container flex items-center gap-2">
            <img
              src="/assets/images/brillabee logo.png"
              alt="Brillabee Logo"
              className={`object-contain ${isElementary ? 'h-10 w-10' : 'h-8 w-8'}`}
            />
            <span className={`font-bold text-indigo-600 ${isElementary ? 'text-2xl' : 'text-xl'}`}>Brillabee</span>
          </div>
        </div>

        <div id="message-list" className="flex-1 overflow-y-auto p-4">
          <div className="welcome-message mb-8 animate-fade-in-up">
            <h1 className={`font-bold text-center text-gray-800 mb-2 ${isElementary ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'}`}>
              {welcomeMessage}
            </h1>
          </div>

          <div className="features-grid grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6">
            {/* Learning Feature Section */}
            <div className={`feature-card bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all ${isElementary ? 'p-5 md:p-7' : 'p-4 md:p-6'}`}>
              <div className="feature-icon text-indigo-600 mb-4">
                <Books size={32} weight="duotone" />
              </div>
              <div className="feature-content">
                <h3 className={`font-bold mb-2 text-gray-800 ${isElementary ? 'text-xl' : 'text-lg'}`}>Learning Help</h3>
                <p className={`text-gray-600 ${isElementary ? 'text-base' : ''}`}>
                  {isElementary
                    ? 'Ask me anything! I can help you with your schoolwork.'
                    : 'Get help with homework, lessons, and understanding concepts at your grade level'}
                </p>
              </div>
            </div>

            {/* Examples Section */}
            <div className="examples-container flex flex-col space-y-2">
              {examples.slice(0, 2).map(example => (
                <div
                  key={example.id}
                  className={`example-card flex items-center bg-white rounded-lg border border-gray-100 shadow-sm cursor-pointer hover:bg-gray-50 hover:shadow-md hover:-translate-y-0.5 transition-all ${isElementary ? 'p-4' : 'p-3'}`}
                  onClick={() => onExampleClick(example.text)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') onExampleClick(example.text); }}
                  aria-label={`Try: ${example.text}`}
                >
                  <div className="example-icon mr-3">
                    <ExampleIcon text={example.icon} bg={example.bg} size={iconSize} />
                  </div>
                  <div className="example-content flex-1">
                    <h4 className={`font-bold text-gray-800 ${isElementary ? 'text-base' : 'text-sm'}`}>{example.title}</h4>
                    <p className={`text-gray-600 truncate ${isElementary ? 'text-base' : 'text-sm'}`}>{example.text}</p>
                  </div>
                  <div className="arrow-icon text-gray-400">
                    <CaretRight size={16} weight="bold" />
                  </div>
                </div>
              ))}
            </div>

            {/* Practice Feature Section */}
            <div className={`feature-card bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:-translate-y-0.5 transition-all ${isElementary ? 'p-5 md:p-7' : 'p-4 md:p-6'}`}>
              <div className="feature-icon text-indigo-600 mb-4">
                <Exam size={32} weight="duotone" />
              </div>
              <div className="feature-content">
                <h3 className={`font-bold mb-2 text-gray-800 ${isElementary ? 'text-xl' : 'text-lg'}`}>Practice & Quizzes</h3>
                <p className={`text-gray-600 ${isElementary ? 'text-base' : ''}`}>
                  {isElementary
                    ? 'Practice what you\'re learning with fun questions!'
                    : 'Get practice problems or quiz yourself on topics you\'re learning'}
                </p>
              </div>
            </div>

            {/* More Examples Section */}
            <div className="examples-container flex flex-col space-y-2">
              {examples.slice(2, 4).map(example => (
                <div
                  key={example.id}
                  className={`example-card flex items-center bg-white rounded-lg border border-gray-100 shadow-sm cursor-pointer hover:bg-gray-50 hover:shadow-md hover:-translate-y-0.5 transition-all ${isElementary ? 'p-4' : 'p-3'}`}
                  onClick={() => onExampleClick(example.text)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') onExampleClick(example.text); }}
                  aria-label={`Try: ${example.text}`}
                >
                  <div className="example-icon mr-3">
                    <ExampleIcon text={example.icon} bg={example.bg} size={iconSize} />
                  </div>
                  <div className="example-content flex-1">
                    <h4 className={`font-bold text-gray-800 ${isElementary ? 'text-base' : 'text-sm'}`}>{example.title}</h4>
                    <p className={`text-gray-600 truncate ${isElementary ? 'text-base' : 'text-sm'}`}>{example.text}</p>
                  </div>
                  <div className="arrow-icon text-gray-400">
                    <CaretRight size={16} weight="bold" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Message Input / Voice Interface */}
        {isVoiceMode ? (
          <VoiceInterface
            gradeLevel={gradeLevel}
            onExitVoice={() => stopVoice()}
          />
        ) : (
          <div className={`message-input-wrapper border-t border-gray-200 ${isElementary ? 'p-5' : 'p-4'}`}>
            <MessageInput
              onSendMessage={onSendMessage}
              disabled={loading}
              gradeLevel={gradeLevel}
              onVoiceToggle={handleVoiceToggle}
            />
          </div>
        )}

      </div>
    </div>
  );
};

export default WelcomeContent;
